import * as React from "react";
import { useCallback } from "react";
import { Field, useFormikContext } from "formik";
import styled, { css } from "styled-components";
import moment from "moment";
import { RELATIVE_RANGE_TYPES } from "views/Constants";
import { Select } from "components/common";
import { isTypeRelativeClassified, RELATIVE_CLASSIFIED_ALL_TIME_RANGE } from "./RelativeTimeRangeClassifiedHelper";
import RelativeRangeValueInput from "./RelativeRangeValueInput";
const RangeWrapper = styled.div`
  flex: 4;
  align-items: center;
  display: grid;
  grid-template-columns: max-content repeat(5, 1fr) max-content;
  grid-template-rows: repeat(2, 1fr) minmax(1.5em, auto);
  gap: 0;
`;
const InputWrap = styled.div`
  grid-area: 2 / 1 / 3 / 3;
  position: relative;

  .form-group {
    margin: 0;
  }
`;
const StyledSelect = styled(Select)`
  grid-area: 2 / 3 / 3 / 7;
  margin: 0 12px;
`;
const RangeTitle = styled.h3`
  grid-area: 1 / 1 / 2 / 2;
`;
const Ago = styled.span(
  ({ theme }) => css`
    grid-area: 2 / 7 / 3 / 8;
    font-size: ${theme.fonts.size.large};

    &::after {
      content: 'ago';
    }
  `
);
const RangeCheck = styled.label(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.small};
    grid-area: 1 / 2 / 2 / 8;
    margin-left: 15px;
    font-weight: normal;
    align-self: self-end;

    &.shortened {
      grid-area: 1 / 2 / 2 / 4;
      text-decoration: line-through;
      cursor: not-allowed;
    }

    input {
      margin-right: 6px;
    }
  `
);
const ErrorMessage = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.variant.dark.danger};
    grid-area: 3 / 1 / 3 / 4;
    font-size: ${theme.fonts.size.small};
    font-style: italic;
    padding: 3px;
  `
);
const buildRangeTypes = (limitDuration) => RELATIVE_RANGE_TYPES.map(({ label, type }) => {
  const typeDuration = moment.duration(1, type).asSeconds();
  if (limitDuration === 0 || typeDuration <= limitDuration) {
    return { label, value: type };
  }
  return null;
}).filter(Boolean);
const RelativeRangeSelectInner = ({
  classifiedRange,
  defaultRange,
  disableUnsetRange,
  disabled,
  error,
  fieldName,
  limitDuration,
  name,
  onChange,
  onUnsetRange,
  title,
  unsetRangeLabel
}) => {
  const { initialValues } = useFormikContext();
  const availableRangeTypes = buildRangeTypes(limitDuration);
  const { isAllTime, value, unit } = classifiedRange;
  const _onChange = useCallback(
    (newClassifiedRange) => {
      onChange({
        target: {
          name,
          value: newClassifiedRange
        }
      });
    },
    [name, onChange]
  );
  const _onChangeTime = useCallback(
    (event) => {
      const inputIsEmpty = event.target.value === "";
      const inputValue = inputIsEmpty ? null : event.target.value;
      _onChange({
        value: inputValue,
        isAllTime,
        unit
      });
    },
    [_onChange, unit, isAllTime]
  );
  const _onChangeUnit = (newUnit) => {
    _onChange({ value, isAllTime, unit: newUnit });
  };
  const _onUnsetRange = (event) => {
    var _a;
    const isUnsetting = event.target.checked;
    const hasInitialRelativeRange = isTypeRelativeClassified(initialValues.timeRangeTabs.relative);
    const _defaultRange = hasInitialRelativeRange && !((_a = initialValues.timeRangeTabs.relative) == null ? void 0 : _a[fieldName].isAllTime) ? initialValues.timeRangeTabs.relative[fieldName] : defaultRange;
    if (isUnsetting && !!onUnsetRange) {
      onUnsetRange();
    }
    _onChange(isUnsetting ? RELATIVE_CLASSIFIED_ALL_TIME_RANGE : _defaultRange);
  };
  return /* @__PURE__ */ React.createElement(RangeWrapper, null, /* @__PURE__ */ React.createElement(RangeTitle, null, title), /* @__PURE__ */ React.createElement(RangeCheck, { htmlFor: `relative-unset-${fieldName}`, className: disableUnsetRange && "shortened" }, /* @__PURE__ */ React.createElement(
    "input",
    {
      "aria-label": unsetRangeLabel,
      checked: isAllTime,
      className: "mousetrap",
      disabled: disableUnsetRange,
      id: `relative-unset-${fieldName}`,
      onChange: _onUnsetRange,
      type: "checkbox",
      value: "0"
    }
  ), unsetRangeLabel), /* @__PURE__ */ React.createElement(InputWrap, null, /* @__PURE__ */ React.createElement(
    RelativeRangeValueInput,
    {
      disabled,
      error,
      fieldName,
      onChange: _onChangeTime,
      unsetRange: isAllTime,
      value
    }
  )), /* @__PURE__ */ React.createElement(
    StyledSelect,
    {
      clearable: false,
      disabled: disabled || isAllTime,
      id: `relative-timerange-${fieldName}-unit`,
      inputProps: { className: "mousetrap" },
      name: `relative-timerange-${fieldName}-unit`,
      onChange: _onChangeUnit,
      options: availableRangeTypes,
      placeholder: "Select a range length",
      value: unit
    }
  ), /* @__PURE__ */ React.createElement(Ago, null), error && /* @__PURE__ */ React.createElement(ErrorMessage, null, error));
};
const RelativeRangeSelect = ({
  defaultRange,
  disableUnsetRange = false,
  disabled = false,
  fieldName,
  limitDuration,
  onUnsetRange = void 0,
  title,
  unsetRangeLabel
}) => /* @__PURE__ */ React.createElement(Field, { name: `timeRangeTabs.relative.${fieldName}` }, ({ field: { value, onChange, name }, meta: { error } }) => /* @__PURE__ */ React.createElement(
  RelativeRangeSelectInner,
  {
    classifiedRange: value,
    defaultRange,
    disableUnsetRange,
    disabled,
    error,
    fieldName,
    limitDuration,
    name,
    onUnsetRange,
    onChange,
    title,
    unsetRangeLabel
  }
));
export default RelativeRangeSelect;
